import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import { IKit } from "../types/kit.types";
import { uploadImagem, removerImagem, definirNomeMidiaKit } from "../services/kitService";
import { useNavigation } from "contexts/notificationsContext";
import { CampoNomeArquivo } from "../../components/CampoNomeArquivo";
import { useMidiaCompartilhada } from "../../hooks/useMidiaCompartilhada";
import { nomeArquivoPadrao } from "../../hooks/useNomeArquivoPadrao";

interface ModalGerenciamentoMidiasProps {
  open: boolean;
  onClose: () => void;
  kit: Partial<IKit>;
  onSave: (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
  }, idAlvo: number) => void;
  onRefreshKit: () => void;
}

const UploadArea: React.FC<{
  arquivoAtualUrl?: string;
  arquivoAtualNome?: string;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  isLoading: boolean;
  idKit?: number;
}> = ({
  arquivoAtualUrl,
  arquivoAtualNome,
  onFileSelect,
  onRemove,
  isLoading,
  idKit,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: false,
    disabled: isLoading || !idKit,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        p: 4,
        textAlign: "center",
        border: isDragActive ? "2px dashed #3C90AD" : "1px solid #e0e0e0",
        backgroundColor: isDragActive ? "#f0f8ff" : "#fff",
        cursor: idKit ? "pointer" : "not-allowed",
        minHeight: 300,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          borderColor: idKit ? "#3C90AD" : "#e0e0e0",
          backgroundColor: idKit ? "#f8f8f8" : "#fff",
        },
        position: "relative",
      }}
    >
      <input {...getInputProps()} />
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {arquivoAtualUrl ? (
        <>
          <Box
            component="img"
            src={arquivoAtualUrl}
            alt="Imagem atual"
            sx={{
              maxWidth: "100%",
              maxHeight: 250,
              mb: 2,
              objectFit: "contain",
            }}
          />
          {arquivoAtualNome && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {arquivoAtualNome}
            </Typography>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={isLoading || !idKit}
          >
            Remover Imagem
          </Button>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ fontSize: 64, color: "#999", mb: 2 }} />
          <Typography variant="body1" mb={1}>
            {idKit
              ? "Arraste uma imagem aqui ou clique para selecionar"
              : "Salve o kit antes de fazer upload de imagens"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Formatos aceitos: PNG, JPG, JPEG (máx. 10MB)
          </Typography>
        </>
      )}
    </Box>
  );
};

export const ModalGerenciamentoMidias: React.FC<ModalGerenciamentoMidiasProps> = ({
  open,
  onClose,
  kit,
  onSave,
  onRefreshKit,
}) => {
  const { openNotification } = useNavigation();
  // FASE 05 — Ajustes 25/08 (item 6): o Kit era o único modal de mídias sem pré-visualização
  // local — guardava só o File e a área de upload continuava mostrando "Arraste uma imagem",
  // como se nada tivesse sido escolhido. `url` guarda o object URL do arquivo selecionado.
  const [imagem, setImagem] = useState<{ file: File | null; nome: string; url?: string }>({
    file: null,
    nome: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Etapa D: avisa quantos outros produtos usam este mesmo nome de arquivo.
  const produtosCompartilhando = useMidiaCompartilhada(
    open, "kits", 2, imagem.nome || kit.imagemNomeArquivo, kit.idKit
  );

  // Etapa C: ao abrir, o campo recebe o nome já gravado; em cadastro NOVO sem nome informado,
  // sugere <código>.PNG. Os outros modais fazem isso no efeito que recarrega do produto —
  // aqui o estado nasce vazio, então o preenchimento vem deste efeito.
  // Reporte de 31/08 (item 6): nome com que o modal ABRIU — a comparação de "nome alterado"
  // é contra ele, não contra o cadastro (a Etapa C já sugere <código>.PNG em produto novo).
  const nomeInicial = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (open) {
      const nome = nomeArquivoPadrao(kit.codigo, ".PNG", !kit.dataCriacao, kit.imagemNomeArquivo);
      nomeInicial.current = nome;
      setImagem({ file: null, nome });
    }
  }, [open, kit]);

  // O object URL é um recurso do browser: liberar ao trocar de arquivo e ao desmontar,
  // senão o blob fica retido enquanto a página viver.
  React.useEffect(() => {
    const urlAtual = imagem.url;
    return () => {
      if (urlAtual) URL.revokeObjectURL(urlAtual);
    };
  }, [imagem.url]);

  const handleFileSelect = (file: File) => {
    // Validar extensão
    const extensoesPermitidas = [".png", ".jpg", ".jpeg"];
    const extensao = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    if (!extensoesPermitidas.includes(extensao)) {
      openNotification({
        message: "Formato inválido. Aceitamos: .PNG, .JPG, .JPEG",
        type: "error",
      });
      return;
    }

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      openNotification({
        message: "Arquivo muito grande. Tamanho máximo: 10MB",
        type: "error",
      });
      return;
    }

    setImagem({ file, nome: file.name, url: URL.createObjectURL(file) });
  };

  const handleRemove = async () => {
    if (!kit.idKit) return;

    setIsLoading(true);
    try {
      const retorno = await removerImagem(kit.idKit);
      if (retorno?.sucesso) {
        openNotification({ message: "Imagem removida com sucesso.", type: "success" });
        onSave({
          imagemUrl: undefined,
          imagemNomeArquivo: undefined,
        }, retorno.id);
        // Plano 21/09/2026 (Frente 3): se foi para a cópia de trabalho, a página navega para ela.
        if (retorno.id === kit.idKit) onRefreshKit();
        setImagem({ file: null, nome: "" });
      } else {
        openNotification({ message: "Erro ao remover imagem.", type: "error" });
      }
    } catch (error) {
      console.error("Erro ao remover imagem:", error);
      openNotification({ message: "Erro ao remover imagem.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvar = async () => {
    if (!kit.idKit) {
      openNotification({
        message: "Salve o kit antes de gerenciar mídias.",
        type: "warning",
      });
      return;
    }

    // Reporte de 31/08 (item 6): sem arquivo novo, mas com o NOME alterado, grava só o nome —
    // é como o produto assume uma mídia que já está no acervo da empresa (F05A-P3b).
    if (!imagem.file) {
      if (imagem.nome === nomeInicial.current || !imagem.nome?.trim()) {
        onClose();
        return;
      }

      setIsLoading(true);
      try {
        const resultado = await definirNomeMidiaKit(kit.idKit!, imagem.nome);
        if (!resultado) return;

        const idAlvo = resultado.id ?? kit.idKit!;
        onSave({ imagemUrl: resultado.url, imagemNomeArquivo: resultado.nomeArquivo }, idAlvo);
        if (idAlvo === kit.idKit) onRefreshKit();
        openNotification(
          resultado.arquivoExiste
            ? { message: "Nome do arquivo salvo com sucesso!", type: "success" }
            : {
                message:
                  `Nome gravado, mas não há arquivo "${resultado.nomeArquivo}" no acervo da empresa. ` +
                  "A imagem só aparecerá quando o arquivo for enviado.",
                type: "warning",
              }
        );
        onClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Etapa D (F05A-P3b): substituir um arquivo compartilhado altera a mídia de TODOS os
    // produtos que o usam — é a intenção do modelo, mas não pode acontecer sem o usuário saber.
    if (produtosCompartilhando > 0) {
      const segue = window.confirm(
        `O arquivo "${imagem.nome}" é usado por ${produtosCompartilhando} outro(s) produto(s). ` +
        `Substituí-lo altera a imagem de todos eles. Deseja continuar?`
      );
      if (!segue) return;
    }

    setIsLoading(true);
    try {
      const retorno = await uploadImagem(kit.idKit!, imagem.file, imagem.nome);
      if (retorno) {
        openNotification({ message: "Imagem salva com sucesso!", type: "success" });
        // Ajustes 25/08 (Etapa A): o servidor grava com o nome informado — é ele que vale.
        const nomeArquivo = imagem.nome;
        onSave({
          imagemUrl: retorno.url,
          imagemNomeArquivo: nomeArquivo,
        }, retorno.id);
        if (retorno.id === kit.idKit) onRefreshKit();
        onClose();
      } else {
        openNotification({ message: "Erro ao fazer upload da imagem.", type: "error" });
      }
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      openNotification({ message: "Erro ao fazer upload da imagem.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Gerenciamento de Mídias</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <UploadArea
          // O arquivo recém-escolhido tem precedência sobre o já salvo (item 6 de 25/08).
          arquivoAtualUrl={imagem.url ?? kit.imagemUrl}
          arquivoAtualNome={imagem.nome || kit.imagemNomeArquivo}
          onFileSelect={handleFileSelect}
          onRemove={handleRemove}
          isLoading={isLoading}
          idKit={kit.idKit}
        />
        {/* Ajustes 25/08 (item 12 do docx de 22/08): é este nome que o servidor grava — e é
            por ele que a importação em massa vincula o arquivo aos produtos. */}
        <CampoNomeArquivo
          valor={imagem.nome || kit.imagemNomeArquivo || ""}
          onChange={(v) => setImagem((a) => ({ ...a, nome: v }))}
          extensoes=".PNG, .JPG, .JPEG"
          desabilitado={isLoading || !kit.idKit}
          produtosCompartilhando={produtosCompartilhando}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isLoading || (!imagem.file && imagem.nome === nomeInicial.current)}
          sx={{ bgcolor: "#3C90AD", "&:hover": { bgcolor: "#2d6f87" } }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
